import React, { useState } from "react";
import { dltService } from "../services/DltService";
import AlertModal from "../components/AlertModal";
import LoadingModal from "../components/LoadingModal";
import withAuthorization from "../components/withAuthorization";
import UnauthorizedComponent from "../components/UnauthorizedComponent";
import Button2 from "../components/Button2";
import { useNavigate } from "react-router-dom";
import { ReactComponent as BackIcon } from "../icons/back.svg";
import "../styles/NodeSyncPage.css";

const NodeSyncPage = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState("Node Sync");

  const handleCheckStatus = async () => {
    if (!userId.trim()) {
      setAlertTitle("Validation Error");
      setAlertMessage("Please enter a User ID (PDL Number or Store Code)");
      setShowAlert(true);
      return;
    }

    setLoading(true);
    try {
      const response = await dltService.getSyncStatus(userId);
      setSyncStatus(response?.data);
      setSyncResult(null);
    } catch (error) {
      setAlertTitle("Error");
      setAlertMessage(error?.response?.data?.message || "Failed to fetch sync status");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await dltService.triggerSync(userId);
      setSyncResult(response?.data);
      setAlertTitle("Success");
      setAlertMessage("Node synchronization completed successfully");
      setShowAlert(true);
    } catch (error) {
      setAlertTitle("Error");
      setAlertMessage(error?.response?.data?.message || "Failed to trigger sync");
      setShowAlert(true);
    } finally {
      setSyncing(false);
    }
  };

  const getNodeStatusBadgeClass = (status) => {
    switch (status) {
      case "healthy":
        return "node-badge-healthy";
      case "in_sync":
        return "node-badge-in-sync";
      case "unhealthy":
        return "node-badge-unhealthy";
      default:
        return "node-badge-unknown";
    }
  };

  const getNodeStatusIcon = (status) => {
    switch (status) {
      case "healthy":
      case "in_sync":
        return "✓";
      case "unhealthy":
        return "✕";
      default:
        return "?";
    }
  };

  return (
    <div className="node-sync-container">
      <AlertModal
        show={showAlert}
        title={alertTitle}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
      <LoadingModal show={loading || syncing} message={syncing ? "Syncing nodes..." : "Checking sync status..."} />

      <div className="node-sync-header">
        <div className="header-back">
          <BackIcon
            style={{ cursor: "pointer", width: "50px", height: "50px" }}
            onClick={() => navigate("/dashboard")}
          />
        </div>
        <div className="header-title">
          <h1>Node Synchronization Status</h1>
          <p>Monitor and synchronize distributed ledger nodes</p>
        </div>
      </div>

      <div className="node-sync-content">
        <div className="input-section">
          <div className="input-group">
            <label htmlFor="userIdInput">User ID</label>
            <input
              id="userIdInput"
              type="text"
              placeholder="Enter PDL Number or Store Code (e.g., DP-490)"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleCheckStatus()}
              className="user-id-input"
            />
          </div>
          <button className="btn-check-status" onClick={handleCheckStatus} disabled={loading}>
            {loading ? "Checking..." : "Check Status"}
          </button>
        </div>

        {syncStatus && (
          <div className="sync-status-section">
            <div className="status-header">
              <h2>Synchronization Status</h2>
              <div className="status-badge" style={{
                background: syncStatus.requiresCorrection
                  ? "linear-gradient(135deg, #ff6b6b 0%, #ff8c8c 100%)"
                  : "linear-gradient(135deg, #51cf66 0%, #69db7c 100%)",
              }}>
                {syncStatus.requiresCorrection ? "⚠ Correction Required" : "✓ All Synced"}
              </div>
            </div>

            <div className="quorum-info">
              <div className="quorum-card">
                <div className="quorum-label">Quorum Status</div>
                <div className="quorum-value" style={{
                  color: syncStatus.quorum === "OK" ? "#51cf66" : "#ff6b6b"
                }}>
                  {syncStatus.quorum}
                </div>
              </div>
              <div className="quorum-card">
                <div className="quorum-label">Majority Hash</div>
                <div className="quorum-value-hash">{syncStatus.majorityHash?.substring(0, 20)}...</div>
              </div>
            </div>

            {syncStatus.outOfSyncNodes && syncStatus.outOfSyncNodes.length > 0 && (
              <div className="out-of-sync-alert">
                <div className="alert-icon">⚠</div>
                <div className="alert-content">
                  <div className="alert-title">{syncStatus.outOfSyncNodes.length} node(s) out of sync</div>
                  <div className="alert-nodes">{syncStatus.outOfSyncNodes.join(", ")}</div>
                </div>
                <button className="btn-sync" onClick={handleSync} disabled={syncing}>
                  {syncing ? "Syncing..." : "Trigger Sync"}
                </button>
              </div>
            )}

            <div className="nodes-grid">
              <h3>Node Details</h3>
              <div className="nodes-list">
                {syncStatus.nodes?.map((node, index) => (
                  <div key={index} className="node-card">
                    <div className="node-header">
                      <div className="node-name">{node.node}</div>
                      <div className={`node-status ${getNodeStatusBadgeClass(node.status)}`}>
                        <span className="status-icon">{getNodeStatusIcon(node.status)}</span>
                        <span className="status-text">{node.status}</span>
                      </div>
                    </div>
                    <div className="node-hash">
                      <div className="hash-label">Hash</div>
                      <div className="hash-value">{node.hash?.substring(0, 30)}...</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {syncResult && (
          <div className="sync-result-section">
            <div className="result-header">
              <h2>Sync Result</h2>
              <div className="result-badge">
                Authority Node: <strong>{syncResult.authorityNode}</strong>
              </div>
            </div>

            <div className="result-nodes-grid">
              {syncResult.nodes?.map((node, index) => (
                <div key={index} className="result-node-card">
                  <div className="result-node-header">
                    <div className="node-name">{node.node}</div>
                    <div className={`node-status ${getNodeStatusBadgeClass(node.status)}`}>
                      <span className="status-icon">{getNodeStatusIcon(node.status)}</span>
                      <span className="status-text">{node.status}</span>
                    </div>
                  </div>
                  <div className="result-node-details">
                    <div className="detail-row">
                      <span className="detail-label">Hash Before:</span>
                      <span className="detail-value">{node.hashBefore?.substring(0, 20)}...</span>
                    </div>
                    {node.hashAfter && (
                      <div className="detail-row">
                        <span className="detail-label">Hash After:</span>
                        <span className="detail-value">{node.hashAfter?.substring(0, 20)}...</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">Records Replayed:</span>
                      <span className="detail-value highlight">{node.recordsReplayed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="result-footer">
              <Button2
                text="Check Another User"
                onClick={() => {
                  setUserId("");
                  setSyncStatus(null);
                  setSyncResult(null);
                }}
              />
            </div>
          </div>
        )}

        {!syncStatus && !syncResult && (
          <div className="empty-state">
            <div className="empty-icon">🔄</div>
            <div className="empty-text">Enter a User ID to check node synchronization status</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuthorization(NodeSyncPage, 37);
